import * as React from "react";
import { useContext } from "react";
import { GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import { DataResult, State } from "@progress/kendo-data-query";
import { useTheme } from "@mui/material";
import StyledKendoGrid from "../../../../../Grid/StyledKendoGrid";
import InboundPipelineGridColumns from "./InboundPipelineGridColumns";
import { SpdrClientViewModel } from "../../../../ViewModel/SpdrClientViewModel";
import InlineEditProps from "../../../../../Grid/InlineEdit/InlineEditProps";
import CommandColumn from "../../../../../Grid/ControlColumn/CommandColumn";
import { CommandDeleteRowButton } from "../../../../../Grid/ControlColumn/CommandDeleteRowButton";
import commandDeleteRow from "../../../../../Grid/ControlColumn/CommandDeleteRow";
import { EditProjectContext } from "../../../EditProjectContext";
import { isProjectReadOnly } from "../../../IsProjectReadOnly";
import { InitialState } from "../../../../../Grid/InitialDataState";
import { removeSpdrOnThisRouteFromRoutingTable } from "./RemoveSpdrOnThisRouteFromRoutingTable";
import { isArrayUndefinedOrEmpty } from "../../../../../Utils/arrays";
import { ReadOnlyProps } from "../../../ReadOnlyProps";

const idField = "SpdrId";

type Props = {
  spdrsOnThisRoute: Array<SpdrClientViewModel>;
  setSpdrsOnThisRoute: (spdrsOnThisRoute: Array<SpdrClientViewModel>) => void;
  odcGsdbsForColumns: Array<string> | undefined;
  ftlRouteNumber: string | undefined;
  volumeDataUpload: React.ReactNode;
};

export default function InboundPipeline({
  spdrsOnThisRoute,
  setSpdrsOnThisRoute,
  odcGsdbsForColumns,
  ftlRouteNumber,
  volumeDataUpload,
  isReadOnly,
}: Props & ReadOnlyProps) {
  const editProjectContext = useContext(EditProjectContext);
  const { project } = editProjectContext;

  const dataResult: DataResult = {
    data: spdrsOnThisRoute,
    total: spdrsOnThisRoute.length,
  };

  const [dataState, setDataState] = React.useState<State>(InitialState);

  const onDataResultChanged = (newResult: DataResult) => {
    setSpdrsOnThisRoute(newResult.data);
  };

  const [editState, setEditState] = React.useState<number>();
  const inlineEditProps = InlineEditProps({
    idField,
    dataResult,
    setEditState,
    onDataReceived: onDataResultChanged,
    isReadonly: isProjectReadOnly(project),
  });

  const commandColumn = CommandColumn({
    // eslint-disable-next-line react/no-unstable-nested-components
    commands: (dataItem: SpdrClientViewModel) => [
      <CommandDeleteRowButton
        disabled={isProjectReadOnly(project) || isReadOnly}
        key="CommandRemoveButton"
        onClick={() => {
          commandDeleteRow({
            idField,
            dataResult,
            setDataResult: onDataResultChanged,
            dataItem,
          });

          removeSpdrOnThisRouteFromRoutingTable(
            dataItem.SupplierGsdb,
            dataItem.PlantGsdb,
            dataItem.DockCode,
            ftlRouteNumber,
            editProjectContext
          );
        }}
      />,
    ],
    showFooterCell: !isArrayUndefinedOrEmpty(spdrsOnThisRoute),
  });

  const theme = useTheme();
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <h4 className="k-h4">
        Inbound Pipeline
        <div
          style={{
            display: "inline-block",
            float: "right",
          }}
        >
          {volumeDataUpload}
        </div>
      </h4>
      <StyledKendoGrid
        muiTheme={theme}
        data={{
          data: dataResult.data
            .filterBy(dataState)
            .orderBy(dataState)
            .paged(dataState)
            .withEditfield(idField, editState),
          total: dataResult.total,
        }}
        sortable
        pageable
        resizable
        {...dataState}
        onDataStateChange={(e: GridDataStateChangeEvent) => {
          setDataState(e.dataState);
        }}
        {...inlineEditProps}
      >
        {[
          ...InboundPipelineGridColumns({
            odcGsdbsForColumns,
            ftlRouteNumber,
            spdrsOnThisRoute,
            isReadOnly,
          }),
          commandColumn,
        ]}
      </StyledKendoGrid>
    </div>
  );
}
